import { api } from "@/lib/api";
import { resolveFieldMap } from "@/lib/featureInfo";

const ringArea = (coords) => {
  const R = 6378137, rad = Math.PI / 180;
  let a = 0;
  for (let i = 0; i < coords.length - 1; i++) { const [x1, y1] = coords[i], [x2, y2] = coords[i + 1]; a += (x2 - x1) * rad * (2 + Math.sin(y1 * rad) + Math.sin(y2 * rad)); }
  return Math.abs((a * R * R) / 2);
};
const areaHa = (g) => {
  if (!g || (g.type !== "Polygon" && g.type !== "MultiPolygon")) return null;
  const rings = g.type === "Polygon" ? [g.coordinates[0]] : g.coordinates.map((p) => p[0]);
  return +(rings.reduce((s, r) => s + ringArea(r), 0) / 10000).toFixed(3);
};
const pick = (props, fm, std) => (fm[std] != null && props[fm[std]] != null && props[fm[std]] !== "" ? String(props[fm[std]]) : "");

// Collect every khasra belonging to `owner` from seeded parcels + all uploaded layers (server + temp).
export async function collectOwnerRecords(owner, tempLayers = []) {
  const needle = owner.trim().toLowerCase();
  const rows = [];
  const [parcels, hits] = await Promise.all([
    api.get("/map/parcels", { params: { q: owner } }).then((r) => r.data?.features || []).catch(() => []),
    api.get("/map/layers/search", { params: { q: owner } }).then((r) => r.data).catch(() => []),
  ]);
  parcels.filter((f) => (f.properties.owner || "").toLowerCase() === needle).forEach((f) => rows.push({ source: "Parcels", khasra: f.properties.khasra, village: f.properties.village, district: f.properties.district, area: f.properties.area_ha, land_type: f.properties.land_type, year: f.properties.survey_year }));
  const fromFeature = (layerName, layer, props, geometry) => {
    const fm = resolveFieldMap(layer, props);
    if (pick(props, fm, "owner").toLowerCase() !== needle) return;
    rows.push({ source: layerName, khasra: pick(props, fm, "khasra") || "—", village: pick(props, fm, "village"), district: pick(props, fm, "district"), area: pick(props, fm, "area_ha") || areaHa(geometry) || "", land_type: pick(props, fm, "land_type"), year: pick(props, fm, "survey_year") });
  };
  hits.forEach((h) => fromFeature(h.layer_name, {}, h.properties, h.geometry));
  tempLayers.filter((l) => l.temp && l.geojson).forEach((l) => l.geojson.features.forEach((f) => fromFeature(l.name, l, f.properties || {}, f.geometry)));
  return rows;
}

export async function exportOwnerReport(owner, rows, { author = "" } = {}) {
  const { jsPDF } = await import("jspdf");
  const pdf = new jsPDF({ unit: "mm", format: "a4" });
  const W = 210, M = 14;
  pdf.setFillColor(15, 81, 50); pdf.rect(0, 0, W, 28, "F");
  pdf.setTextColor(255); pdf.setFontSize(18); pdf.setFont("helvetica", "bold"); pdf.text("CG Land Geoportal", M, 12);
  pdf.setFontSize(11); pdf.setFont("helvetica", "normal"); pdf.text("Owner Lookup Report", M, 20);
  pdf.setTextColor(30); pdf.setFontSize(15); pdf.setFont("helvetica", "bold"); pdf.text(owner, M, 40);
  pdf.setFontSize(9); pdf.setFont("helvetica", "normal"); pdf.setTextColor(110);
  const totalArea = rows.reduce((s, r) => s + (parseFloat(r.area) || 0), 0);
  pdf.text(`${rows.length} khasra${rows.length === 1 ? "" : "s"} · total ${totalArea.toFixed(2)} ha · generated ${new Date().toLocaleString("en-IN")}${author ? ` · ${author}` : ""}`, M, 46);
  const cols = [["#", 8], ["Khasra", 22], ["Village", 32], ["District", 26], ["Area (ha)", 20], ["Land type", 26], ["Year", 12], ["Source layer", 36]];
  let y = 56;
  const header = () => {
    pdf.setFillColor(236, 253, 245); pdf.rect(M, y - 5, W - 2 * M, 7, "F");
    pdf.setFont("helvetica", "bold"); pdf.setFontSize(8.5); pdf.setTextColor(6, 78, 59);
    let x = M + 1; cols.forEach(([c, w]) => { pdf.text(c, x, y); x += w; }); y += 6;
    pdf.setFont("helvetica", "normal"); pdf.setTextColor(30);
  };
  header();
  rows.forEach((r, i) => {
    if (y > 282) { pdf.addPage(); y = 20; header(); }
    const vals = [String(i + 1), r.khasra, r.village, r.district, r.area !== "" && r.area != null ? String(r.area) : "", r.land_type, String(r.year || ""), r.source];
    let x = M + 1;
    if (i % 2) { pdf.setFillColor(248, 250, 252); pdf.rect(M, y - 4.5, W - 2 * M, 6, "F"); }
    vals.forEach((v, j) => { pdf.text(pdf.splitTextToSize(v || "—", cols[j][1] - 2)[0] || "", x, y); x += cols[j][1]; });
    y += 6;
  });
  pdf.setFontSize(8); pdf.setTextColor(140); pdf.text("Data as uploaded to CG Land Geoportal; verify against official revenue records.", M, 292);
  pdf.save(`owner-${owner.replace(/[^\w]+/g, "-").toLowerCase()}-khasras.pdf`);
}
